// demoDataGenerator.js - Helper functions voor demo team generatie
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Faker.js implementatie - Generate realistic Dutch business data
 */
const faker = {
  // Nederlandse bedrijfsnamen componenten
  company: {
    adjectives: ['Innovatieve', 'Strategische', 'Dynamische', 'Creatieve', 'Agile', 'Digitale', 'Moderne', 'Efficiënte', 'Flexibele', 'Proactieve'],
    nouns: ['Marketing', 'Sales', 'Product', 'Development', 'Support', 'Finance', 'HR', 'Operations', 'Strategy', 'Innovation'],
    suffixes: ['Team', 'Squad', 'Unit', 'Groep', 'Divisie', 'Afdeling'],
    
    name: () => {
      const adj = faker.company.adjectives[Math.floor(Math.random() * faker.company.adjectives.length)];
      const noun = faker.company.nouns[Math.floor(Math.random() * faker.company.nouns.length)];
      const suffix = faker.company.suffixes[Math.floor(Math.random() * faker.company.suffixes.length)];
      return `${adj} ${noun} ${suffix}`;
    },
    
    catchPhrase: () => {
      const phrases = [
        'Samen sterker, samen beter',
        'Innovatie door samenwerking',
        'Excellence in teamwork',
        'Resultaat door vertrouwen',
        'Transparantie en groei',
        'Verbinding maakt het verschil',
        'Van visie naar resultaat',
        'Teamwork makes the dream work'
      ];
      return phrases[Math.floor(Math.random() * phrases.length)];
    }
  },

  // Nederlandse namen database
  person: {
    firstNames: [
      'Emma', 'Noah', 'Tess', 'Luca', 'Sophie', 'Daan', 'Zoë', 'Sem', 'Sara', 'Lucas',
      'Julia', 'Jayden', 'Anna', 'Mason', 'Noa', 'Bram', 'Eva', 'Finn', 'Mila', 'Ruben',
      'Lisa', 'Jesse', 'Isa', 'Milan', 'Liv', 'Gijs', 'Fleur', 'Thijs', 'Saar', 'Levi',
      'Roos', 'Lars', 'Noor', 'Teun', 'Fenna', 'Joep', 'Iris', 'Ties', 'Luna', 'Stijn'
    ],
    
    lastNames: [
      'de Jong', 'Jansen', 'de Vries', 'van den Berg', 'van Dijk', 'Bakker', 'Janssen', 'Visser',
      'Smit', 'Meijer', 'de Boer', 'Mulder', 'de Groot', 'Bos', 'Vos', 'Peters', 'Hendriks',
      'van Leeuwen', 'Dekker', 'Brouwer', 'de Wit', 'Dijkstra', 'Smits', 'de Graaf', 'van der Meer',
      'van der Linden', 'Kok', 'Verhoeven', 'van den Broek', 'de Haan'
    ],
    
    fullName: () => {
      const first = faker.person.firstNames[Math.floor(Math.random() * faker.person.firstNames.length)];
      const last = faker.person.lastNames[Math.floor(Math.random() * faker.person.lastNames.length)];
      return `${first} ${last}`;
    }
  },

  // Email generator
  internet: {
    domainSuffixes: ['nl', 'com', 'eu'],
    domains: ['example', 'test', 'demo', 'fictief', 'sample'],
    
    email: (name = null) => {
      if (!name) name = faker.person.fullName();
      const cleanName = name.toLowerCase()
        .replace(/\s+/g, '.')
        .replace(/[áàäâ]/g, 'a')
        .replace(/[éèëê]/g, 'e')
        .replace(/[íìïî]/g, 'i')
        .replace(/[óòöô]/g, 'o')
        .replace(/[úùüû]/g, 'u')
        .replace(/[^a-z.]/g, '');
      
      const domain = faker.internet.domains[Math.floor(Math.random() * faker.internet.domains.length)];
      const suffix = faker.internet.domainSuffixes[Math.floor(Math.random() * faker.internet.domainSuffixes.length)];
      
      return `${cleanName}@${domain}.${suffix}`;
    }
  },

  // Number generators
  number: {
    int: (options = {}) => {
      const min = options.min || 0;
      const max = options.max || 100;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }
  }
};

/**
 * Generate realistic Lencioni quiz scores
 * Simulates different team dysfunction patterns
 */
const generateQuizScores = (pattern = 'random') => {
  const patterns = {
    // Hoog presterend team
    high_performing: {
      vertrouwen: () => faker.number.int({min: 40, max: 50}) / 10,
      conflicten: () => faker.number.int({min: 35, max: 45}) / 10,
      betrokkenheid: () => faker.number.int({min: 40, max: 50}) / 10,
      verantwoording: () => faker.number.int({min: 38, max: 48}) / 10,
      resultaten: () => faker.number.int({min: 42, max: 50}) / 10
    },
    
    // Team met vertrouwensissues
    trust_issues: {
      vertrouwen: () => faker.number.int({min: 15, max: 25}) / 10,
      conflicten: () => faker.number.int({min: 20, max: 30}) / 10,
      betrokkenheid: () => faker.number.int({min: 25, max: 35}) / 10,
      verantwoording: () => faker.number.int({min: 30, max: 40}) / 10,
      resultaten: () => faker.number.int({min: 35, max: 45}) / 10
    },
    
    // Team met conflictvermijding
    conflict_avoidance: {
      vertrouwen: () => faker.number.int({min: 35, max: 45}) / 10,
      conflicten: () => faker.number.int({min: 15, max: 25}) / 10,
      betrokkenheid: () => faker.number.int({min: 25, max: 35}) / 10,
      verantwoording: () => faker.number.int({min: 30, max: 40}) / 10,
      resultaten: () => faker.number.int({min: 32, max: 42}) / 10
    },
    
    // Gemiddeld team
    average: {
      vertrouwen: () => faker.number.int({min: 30, max: 40}) / 10,
      conflicten: () => faker.number.int({min: 28, max: 38}) / 10,
      betrokkenheid: () => faker.number.int({min: 30, max: 40}) / 10,
      verantwoording: () => faker.number.int({min: 28, max: 38}) / 10,
      resultaten: () => faker.number.int({min: 30, max: 40}) / 10
    },
    
    // Random variatie
    random: {
      vertrouwen: () => faker.number.int({min: 20, max: 45}) / 10,
      conflicten: () => faker.number.int({min: 20, max: 45}) / 10,
      betrokkenheid: () => faker.number.int({min: 20, max: 45}) / 10,
      verantwoording: () => faker.number.int({min: 20, max: 45}) / 10,
      resultaten: () => faker.number.int({min: 20, max: 45}) / 10
    }
  };

  const selectedPattern = patterns[pattern] || patterns.random;
  
  return {
    vertrouwen: Number(selectedPattern.vertrouwen().toFixed(1)),
    conflicten: Number(selectedPattern.conflicten().toFixed(1)),
    betrokkenheid: Number(selectedPattern.betrokkenheid().toFixed(1)),
    verantwoording: Number(selectedPattern.verantwoording().toFixed(1)),
    resultaten: Number(selectedPattern.resultaten().toFixed(1))
  };
};

/**
 * Generate individual quiz responses from trede scores
 * Each trede has 5 questions, scale 1-5
 */
const generateQuizResponses = (tredeScores) => {
  const responses = {};
  
  Object.keys(tredeScores).forEach(trede => {
    const baseScore = tredeScores[trede];
    
    // Generate 5 responses per trede with variation around base score
    for (let i = 0; i < 5; i++) {
      const variation = (Math.random() - 0.5) * 1.5; // ±0.75 variation
      let score = Math.round(baseScore + variation);
      
      // Ensure score is within 1-5 range
      score = Math.max(1, Math.min(5, score));
      
      responses[`${trede}_${i}`] = score;
    }
  });
  
  return responses;
};

/**
 * Generate realistic team member data
 */
const generateTeamMembers = (teamSize) => {
  const members = [];
  const usedNames = new Set();
  
  for (let i = 0; i < teamSize; i++) {
    let name;
    let email;
    
    // Ensure unique names
    do {
      name = faker.person.fullName();
    } while (usedNames.has(name));
    
    usedNames.add(name);
    email = faker.internet.email(name);
    
    members.push({
      name,
      email,
      pattern: ['high_performing', 'average', 'trust_issues', 'conflict_avoidance', 'random'][Math.floor(Math.random() * 5)]
    });
  }
  
  return members;
};

/**
 * Generate invite code (9 characters: ABC-123-XYZ)
 */
const generateInviteCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const segments = [];
  
  for (let segment = 0; segment < 3; segment++) {
    let code = '';
    for (let i = 0; i < 3; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    segments.push(code);
  }
  
  return segments.join('-');
};

/**
 * Main function: Generate single demo team with members and responses
 */
export const generateSingleDemoTeam = async (userEmail, organizationId, options = {}) => {
  const {
    withResponses = true,
    teamSize = faker.number.int({min: 5, max: 15}),
    teamPattern = 'random'
  } = options;

  try {
    console.log(`🎲 Generating demo team (${teamSize} members)...`);

    // 1. Generate team data
    const teamData = {
      name: faker.company.name(),
      description: faker.company.catchPhrase(),
      createdBy: userEmail.split('@')[0], // Extract name from email
      createdByEmail: userEmail,
      organizationId: organizationId,
      createdAt: serverTimestamp(),
      status: 'active',
      measurementPeriod: getCurrentQuarter()
    };

    // 2. Create team in database
    console.log('📝 Creating team document...');
    const teamRef = await addDoc(collection(db, 'teams'), teamData);
    console.log(`✅ Team created: ${teamData.name} (${teamRef.id})`);

    // 3. Generate team members
    const members = generateTeamMembers(teamSize);
    const invitations = [];

    // 4. Create invitations for each member
    console.log('📧 Creating invitations...');
    for (const member of members) {
      const inviteCode = generateInviteCode();
      
      // Add to team invites subcollection
      const invitationRef = await addDoc(collection(db, 'teams', teamRef.id, 'invites'), {
        email: member.email,
        code: inviteCode,
        used: withResponses, // Mark as used if generating responses
        createdAt: serverTimestamp(),
        completedAt: withResponses ? serverTimestamp() : null
      });

      invitations.push({
        id: invitationRef.id,
        email: member.email,
        code: inviteCode,
        pattern: member.pattern
      });

      console.log(`✅ Invitation: ${member.name} (${inviteCode})`);
    }

    // 5. Generate quiz responses if requested
    if (withResponses) {
      console.log('📊 Generating quiz responses...');
      
      for (const invitation of invitations) {
        const tredeScores = generateQuizScores(invitation.pattern);
        const responses = generateQuizResponses(tredeScores);

        // Add response to database
        await addDoc(collection(db, 'responses'), {
          teamId: teamRef.id,
          email: invitation.email,
          invitationId: invitation.id,
          tredeScores: tredeScores,
          responses: responses,
          completedAt: serverTimestamp(),
          inviteCode: invitation.code
        });

        console.log(`✅ Response: ${invitation.email} (${invitation.pattern})`);
      }
    }

    return {
      teamId: teamRef.id,
      teamName: teamData.name,
      teamSize: members.length,
      hasResponses: withResponses,
      members: members.map(m => ({
        name: m.name,
        email: m.email
      }))
    };

  } catch (error) {
    console.error('❌ Error generating demo team:', error);
    throw error;
  }
};

/**
 * Generate multiple demo teams with progress tracking
 */
export const generateMultipleDemoTeams = async (userEmail, organizationId, options = {}) => {
  const {
    teamCount = 5,
    withResponses = true,
    onProgress = null
  } = options;

  const results = [];
  
  try {
    console.log(`🚀 Starting generation of ${teamCount} demo teams...`);
    
    for (let i = 0; i < teamCount; i++) {
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: teamCount,
          phase: 'creating',
          message: `Creating team ${i + 1}/${teamCount}...`
        });
      }

      // Vary team sizes and patterns for diversity
      const teamOptions = {
        withResponses,
        teamSize: faker.number.int({min: 5, max: 20}),
        teamPattern: ['high_performing', 'average', 'trust_issues', 'conflict_avoidance', 'random'][i % 5]
      };

      const result = await generateSingleDemoTeam(userEmail, organizationId, teamOptions);
      results.push(result);
      
      console.log(`✅ Generated team ${i + 1}/${teamCount}: ${result.teamName}`);
    }

    if (onProgress) {
      onProgress({
        current: teamCount,
        total: teamCount,
        phase: 'completed',
        message: `Successfully created ${teamCount} demo teams!`
      });
    }

    return {
      success: true,
      teamsCreated: teamCount,
      teams: results,
      totalMembers: results.reduce((sum, team) => sum + team.teamSize, 0)
    };

  } catch (error) {
    console.error('❌ Error generating multiple teams:', error);
    
    if (onProgress) {
      onProgress({
        current: results.length,
        total: teamCount,
        phase: 'error',
        message: `Error after ${results.length} teams: ${error.message}`
      });
    }
    
    throw error;
  }
};

/**
 * Utility: Get current quarter for measurement period
 */
const getCurrentQuarter = () => {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${quarter} ${now.getFullYear()}`;
};

/**
 * Utility: Clean database - remove all demo teams for organization
 */
export const cleanDemoData = async (organizationId) => {
  console.log(`🧹 Cleaning demo data for organization: ${organizationId}`);
  
  // Note: This would require admin privileges in production
  // For now, return a placeholder
  console.log('⚠️ Clean function requires admin implementation');
  
  return {
    success: true,
    message: 'Demo data cleanup initiated'
  };
};

/**
 * Export demo generation presets
 */
export const DEMO_PRESETS = {
  small: {
    teamCount: 3,
    teamSize: { min: 5, max: 8 },
    description: '3 kleine teams (5-8 leden)'
  },
  medium: {
    teamCount: 5,
    teamSize: { min: 8, max: 15 },
    description: '5 middelgrote teams (8-15 leden)'
  },
  large: {
    teamCount: 10,
    teamSize: { min: 10, max: 20 },
    description: '10 grote teams (10-20 leden)'
  },
  enterprise: {
    teamCount: 25,
    teamSize: { min: 5, max: 25 },
    description: '25 diverse teams (5-25 leden)'
  }
};